<template>
  <main class="mx-auto w-full max-w-[1320px] px-4 py-6 font-inter text-slate-900">
    <template v-if="loading">
      <section class="rounded-lg border border-slate-200 bg-white p-6">
        <div class="space-y-4">
          <div class="h-4 w-44 animate-pulse rounded bg-slate-100"></div>
          <div class="h-10 w-3/4 animate-pulse rounded bg-slate-100"></div>
          <div class="h-5 w-2/3 animate-pulse rounded bg-slate-100"></div>
          <div class="grid gap-3 pt-3 md:grid-cols-4">
            <div v-for="item in 4" :key="item" class="h-24 animate-pulse rounded-lg bg-slate-100"></div>
          </div>
        </div>
      </section>
    </template>

    <template v-else>
      <section class="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
        <div class="h-1.5 bg-newPrimary"></div>
        <div class="grid gap-7 p-6 lg:grid-cols-[minmax(0,0.85fr)_minmax(520px,1.15fr)] lg:items-end lg:p-8">
          <div>
            <p class="text-lg font-semibold text-slate-950 md:text-xl">
              Welcome to Screened{{ profile.first_name ? `, ${profile.first_name}` : '' }}
            </p>

            <h1 class="mt-7 text-2xl font-bold leading-tight text-slate-950 md:text-[1.7rem]">Explore opportunities and employers in your market</h1>
            <p class="mt-2 max-w-2xl text-base leading-7 text-slate-600">
              Track hiring activity, companies and projects relevant to your experience and career interests.
            </p>
          </div>

          <div>
            <p class="mb-3 text-sm font-semibold text-slate-800">Based on your profile, we've identified:</p>
            <div class="grid gap-3 sm:grid-cols-2">
              <div
                v-for="item in clientSummaryItems"
                :key="item.label"
                class="flex min-h-[76px] items-start gap-3 rounded-lg border border-slate-200 bg-[#f8fbfc] px-4 py-3"
              >
                <div class="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-white text-newPrimary ring-1 ring-newPrimary/15">
                  <component :is="item.icon" class="h-4 w-4" />
                </div>
                <p class="text-sm leading-6 text-slate-700">
                  <span class="block text-lg font-bold leading-5 text-slate-950">{{ item.value }}</span>
                  <span>{{ item.label }}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <CompanyMatchesPanel v-if="canUseCompanyMatches" id="company-matches" class="mt-8" :profile="profile" />

      <section class="mt-8">
        <div class="mb-4">
          <h2 class="text-xl font-bold text-slate-950">Jobs Similar to Your Experience</h2>
          <p class="mt-1 text-sm text-slate-600">{{ matchingOpportunityCopy }}</p>
        </div>

        <div v-if="jobPreview.length" class="grid gap-4 lg:grid-cols-3">
          <router-link
            v-for="job in jobPreview.slice(0, 3)"
            :key="job.job_id"
            :to="{ name: 'ContractorViewJob', params: { jobId: job.job_slug || '#' } }"
            class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-newPrimary/50 hover:shadow-md"
            @click="trackJobClick(job, 'matching_opportunities')"
          >
            <p class="line-clamp-2 text-base font-semibold text-slate-950">{{ job.job_title }}</p>
            <p class="mt-1 text-sm font-medium text-slate-700">{{ job.company_name }}</p>
            <p class="mt-1 flex items-center gap-2 text-sm text-slate-500">
              <MapPin class="h-4 w-4 text-slate-400" />
              {{ job.job_location || 'Location not listed' }}
            </p>
            <p v-if="job.closing_date" class="mt-1 text-sm font-medium text-slate-500">
              Closes {{ formatDate(job.closing_date) }}
            </p>
            <p v-if="getCompanyResearchForJob(job)" class="mt-4 line-clamp-3 border-t border-slate-100 pt-3 text-sm text-slate-600">
              {{ getCompanyResearchForJob(job) }}
            </p>
          </router-link>
        </div>
        <router-link
          v-if="jobPreview.length"
          :to="{ name: 'ContractorJobs' }"
          class="mt-4 inline-flex text-sm font-semibold text-newPrimary hover:underline"
        >
          See all jobs
        </router-link>

        <div v-else class="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
          <h3 class="text-base font-semibold text-slate-950">No matching roles are available today</h3>
          <p class="mt-1 text-sm text-slate-600">This page will update as new roles are added in your field.</p>
        </div>
      </section>

      <section v-if="hiringCompanies.length" class="mt-8">
        <div class="mb-4">
          <h2 class="text-xl font-bold text-slate-950">Companies Hiring People Like You</h2>
          <p class="mt-1 text-sm text-slate-600">
            Research employers with active roles and see the company details behind the job.
          </p>
        </div>

        <div class="grid gap-4 lg:grid-cols-3">
          <router-link
            v-for="company in hiringCompanyPreview"
            :key="company.id || company.slug"
            :to="{ name: 'ContractorCompanyPage', params: { slug: company.slug } }"
            class="rounded-lg border border-slate-200 bg-white p-4 shadow-sm transition hover:border-newPrimary/50 hover:shadow-md"
            @click="trackCompanyClick(company, 'currently_hiring')"
          >
            <div class="flex items-start justify-between gap-3">
              <div class="flex min-w-0 items-start gap-3">
                <div class="flex h-11 w-11 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-slate-100 text-sm font-bold text-slate-500 ring-1 ring-slate-200">
                  <img v-if="company.logo" :src="company.logo" :alt="company.name" class="h-full w-full object-contain" />
                  <span v-else>{{ companyInitial(company.name) }}</span>
                </div>
                <div class="min-w-0">
                  <p class="line-clamp-2 text-base font-semibold text-slate-950">{{ company.name }}</p>
                  <p class="mt-1 line-clamp-1 text-sm text-slate-500">{{ company.office_address || company.industry_subcategory || company.industry || 'Company profile' }}</p>
                </div>
              </div>
              <span class="rounded-full bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Hiring</span>
            </div>

            <div class="mt-4 grid grid-cols-2 gap-2 text-sm">
              <div class="rounded-md bg-slate-50 p-2">
                <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Live Jobs</p>
                <p class="mt-1 font-semibold text-slate-800">{{ formatCompanyJobsCount(company) }}</p>
              </div>
              <div class="rounded-md bg-slate-50 p-2">
                <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Cash in Bank</p>
                <p class="mt-1 font-semibold text-slate-800">{{ formatMoney(company.cash_in_bank) }}</p>
              </div>
            </div>

            <div v-if="getCompanyProjectCount(company) > 0" class="mt-2 rounded-md bg-slate-50 p-2 text-sm">
              <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">Projects</p>
              <p class="mt-1 font-semibold text-slate-800">{{ formatCompanyProjectsCount(company) }}</p>
            </div>

            <div v-if="company.commodities && company.commodities.length" class="mt-3 flex flex-wrap gap-1.5">
              <span
                v-for="commodity in company.commodities.slice(0, 4)"
                :key="`${company.id}-${commodity}`"
                class="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-semibold text-slate-600"
              >
                {{ commodity }}
              </span>
            </div>
          </router-link>
        </div>
        <router-link
          :to="{ name: 'ContractorCompanySearch', query: hiringCompaniesQuery }"
          class="mt-4 inline-flex text-sm font-semibold text-newPrimary hover:underline"
        >
          See all companies
        </router-link>
      </section>

      <section class="mt-8 rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
        <h2 class="text-xl font-bold text-slate-950">{{ clientActivityTitle }}</h2>
        <ul class="mt-4 space-y-2 text-sm text-slate-700">
          <li v-for="item in clientWhatsNewItems" :key="item.label" class="flex items-center gap-2">
            <span class="h-1.5 w-1.5 shrink-0 rounded-full bg-newPrimary"></span>
            <span class="leading-5">
              <span class="font-bold text-slate-950">{{ item.value }}</span>
              {{ item.label }}
            </span>
          </li>
        </ul>
        <pre
          v-if="showCompanyMonthlyActivityDebug"
          class="mt-4 max-h-64 overflow-auto rounded-md bg-slate-950 p-3 text-xs leading-5 text-slate-100"
        >{{ companyMonthlyActivityDebug }}</pre>
      </section>
    </template>
  </main>
</template>

<script>
let apiEndpoint = process.env.VUE_APP_API_BASEURL

import contractorService from '@/services/marketplace/contractor.service'
import genericService from '@/services/marketplace/generic.service'
import CompanyMatchesPanel from '@/components/marketplace/contractor/CompanyMatchesPanel.vue'
import {
  getContractorCompanyRecentActivity,
  getContractorMiningCompanies,
  getContractorMiningCompanySearchAccess,
} from '@/api/contractor/company'
import {
  BadgeDollarSign,
  BriefcaseBusiness,
  Building2,
  CheckCircle2,
  Download,
  FileText,
  Gem,
  MapPin,
  MapPinned,
  Pencil,
  Search,
  Share2,
  Sparkles,
  Star,
  UploadCloud,
} from 'lucide-vue-next'

export default {
  name: 'MarketplaceContractorProfileEdit',
  inject: ['toggleLoading'],
  components: {
    BadgeDollarSign,
    BriefcaseBusiness,
    Building2,
    CheckCircle2,
    CompanyMatchesPanel,
    Download,
    FileText,
    Gem,
    MapPin,
    MapPinned,
    Pencil,
    Search,
    Share2,
    Sparkles,
    Star,
    UploadCloud,
  },
  data() {
    return {
      loading: true,
      loadingAIDescription: false,
      profile: {},
      responses: [],
      uploads: {
        active: null,
        replacement: null,
        pending: null,
        failed: null,
      },
      jobs: [],
      applications: [],
      canSearchCompanies: false,
      companyAccessLoaded: false,
      bookmarkedCompanies: [],
      marketCompanies: [],
      hiringCompanies: [],
      marketProjects: [],
      savedSimilarCompanies: [],
      marketTotals: {
        companies: null,
        hiringCompanies: null,
        projects: null,
      },
      companyMonthlyActivity: {
        recent_key_hires: 0,
        capital_raisings: 0,
        matching_companies: 0,
        industries: [],
        days: 30,
        start_date: null,
        end_date: null,
      },
      companyMonthlyActivityRequest: null,
      companyMonthlyActivityError: null,
      profilePicture: 'https://via.placeholder.com/128',
    }
  },
  computed: {
    locationLabel() {
      return [this.profile.suburb, this.profile.state].filter(Boolean).join(', ')
    },
    roleLabel() {
      return this.profile.role || 'your role'
    },
    industryLabel() {
      return this.profile.industry || 'your industry'
    },
    companyDirectoryIndustryFilter() {
      return this.normalizeCompanyDirectoryIndustry(this.profile.industry)
    },
    canUseCompanyMatches() {
      const industryLabel = String(this.profile.industry || '').trim().toLowerCase()
      const industryId = Number(this.profile.industryId || this.profile.industry_id || 0)

      return this.companyDirectoryIndustryFilter === 'mining'
        || industryLabel === 'mining'
        || industryId === 3
    },
    homeStateFilterValue() {
      return this.normalizeAustralianState(this.profile.state)
    },
    homeStateLabel() {
      return this.homeStateFilterValue || this.profile.state
    },
    hasActiveResume() {
      return !!this.uploads.active || !!this.profile.has_resume
    },
    resumeNeedsAttention() {
      return !this.hasActiveResume || !!this.uploads.failed || !!this.uploads.pending
    },
    hiringCompaniesCount() {
      return typeof this.marketTotals.hiringCompanies === 'number' && this.marketTotals.hiringCompanies > 0
        ? this.marketTotals.hiringCompanies
        : this.hiringCompanies.length
    },
    activeJobsInIndustryCount() {
      if (!this.companyDirectoryIndustryFilter) {
        return this.jobs.length
      }

      return this.jobs.filter((job) => this.normalizeJobIndustry(job) === this.companyDirectoryIndustryFilter).length
    },
    clientSummaryItems() {
      const items = [
        {
          value: this.activeJobsInIndustryCount.toLocaleString(),
          label: `active ${this.activeJobsInIndustryCount === 1 ? 'job' : 'jobs'} in the ${this.industryLabel} industry`,
          icon: Search,
        },
        {
          value: this.hiringCompaniesCount.toLocaleString(),
          label: `${this.hiringCompaniesCount === 1 ? 'company' : 'companies'} hiring for ${this.industryLabel} jobs`,
          icon: Building2,
        },
      ]

      if (this.canSearchCompanies) {
        items.push({
          value: this.formatCount(this.marketTotals.companies, this.marketCompanies.length),
          label: `${this.industryLabel.toLowerCase()} companies in your home state${this.homeStateLabel ? ` of ${this.homeStateLabel}` : ''}`,
          icon: BriefcaseBusiness,
        })

        items.push({
          value: this.formatCount(this.marketTotals.projects, this.marketProjects.length),
          label: `active projects in your home state${this.homeStateLabel ? ` of ${this.homeStateLabel}` : ''}`,
          icon: MapPinned,
        })
      }

      return items
    },
    clientActivityTitle() {
      return 'Market Activity This Month'
    },
    recentKeyHiresThisMonth() {
      return Number(this.companyMonthlyActivity.recent_key_hires || 0)
    },
    capitalRaisingsThisMonth() {
      return Number(this.companyMonthlyActivity.capital_raisings || 0)
    },
    showCompanyMonthlyActivityDebug() {
      return this.$route?.query?.debug_activity === '1'
    },
    companyMonthlyActivityDebug() {
      return JSON.stringify({
        request: this.companyMonthlyActivityRequest,
        response: this.companyMonthlyActivity,
        error: this.companyMonthlyActivityError,
        profileIndustry: this.profile.industry || null,
        profileIndustryId: this.profile.industryId || null,
      }, null, 2)
    },
    clientWhatsNewItems() {
      const items = []

      if (this.recentKeyHiresThisMonth > 0) {
        items.push({
          value: this.recentKeyHiresThisMonth,
          label: `${this.recentKeyHiresThisMonth === 1 ? 'Executive Appointment' : 'Executive Appointments'} Announced`,
        })
      }

      if (this.capitalRaisingsThisMonth > 0) {
        items.push({
          value: this.capitalRaisingsThisMonth,
          label: `${this.capitalRaisingsThisMonth === 1 ? 'Capital Raising' : 'Capital Raisings'} Completed`,
        })
      }

      if (this.hiringCompanies.length > 0) {
        items.push({
          value: this.trustedHiringEmployerCount,
          label: `${this.trustedHiringEmployerCount === 1 ? 'Company Currently Hiring' : 'Companies Currently Hiring'}`,
        })
      }

      if (!items.length && this.marketProjects.length > 0) {
        items.push({
          value: this.marketProjects.length,
          label: `${this.marketProjects.length === 1 ? 'project' : 'projects'} worth watching in your home state`,
        })
      }

      if (!items.length) {
        items.push({
          value: 'Activity',
          label: 'will appear here when key hires or capital raisings are dated within the last 30 days',
        })
      }

      return items
    },
    welcomeHeadline() {
      if (this.canSearchCompanies && this.profile.state) {
        return `${this.industryLabel} market activity in ${this.profile.state}`
      }
      if (this.canSearchCompanies) {
        return `${this.industryLabel} market activity`
      }
      if (this.profile.role) {
        return `${this.roleLabel} opportunities to keep an eye on`
      }
      return 'Your market is starting to take shape'
    },
    welcomeSubtext() {
      if (this.canSearchCompanies) {
        return 'Screened found companies, active jobs, projects, and roles connected to your market. Check back as new roles appear.'
      }
      if (this.jobs.length) {
        return 'Screened found roles connected to your profile. Check back as new matching jobs appear.'
      }
      return 'As your profile details improve, Screened can surface more useful market activity.'
    },
    marketPulseTitle() {
      if (this.profile.state && this.canSearchCompanies) {
        return `A quick read on ${this.profile.state}`
      }
      return 'A quick read on your market'
    },
    marketPulseCopy() {
      if (this.canSearchCompanies) {
        return 'Live counts from the company, jobs, and project data Screened can see today.'
      }
      return 'Live counts from the role data Screened can see today.'
    },
    trustedHiringEmployerCount() {
      return this.hiringCompaniesCount
    },
    pulseItems() {
      const items = []

      if (this.canSearchCompanies) {
        if ((this.marketTotals.companies || this.marketCompanies.length) > 0) {
          items.push({
            label: this.profile.state ? `Employers in ${this.profile.state}` : 'Employers surfaced',
            value: this.formatCount(this.marketTotals.companies, this.marketCompanies.length),
            caption: 'From company intelligence',
            icon: Building2,
          })
        }

        if (this.trustedHiringEmployerCount > 0) {
          items.push({
            label: 'Hiring employers',
            value: this.trustedHiringEmployerCount,
            caption: 'Loaded with active jobs',
            icon: BriefcaseBusiness,
          })
        }

        if ((this.marketTotals.projects || this.marketProjects.length) > 0) {
          items.push({
            label: 'Projects surfaced',
            value: this.formatCount(this.marketTotals.projects, this.marketProjects.length),
            caption: 'From company profiles',
            icon: MapPinned,
          })
        }

        if (this.topCommodities.length) {
          items.push({
            label: 'Top theme',
            value: this.topCommodities[0].name,
            caption: 'Most visible commodity signal',
            icon: Gem,
          })
        }
      }

      if (this.jobs.length) {
        items.push({
          label: 'Matching jobs',
          value: this.jobs.length,
          caption: this.profile.role ? `For ${this.roleLabel}` : 'From your profile',
          icon: Search,
        })
      }

      if (this.newJobsThisWeek.length) {
        items.push({
          label: 'New this week',
          value: this.newJobsThisWeek.length,
          caption: 'Recent matching roles',
          icon: Sparkles,
        })
      }

      if (this.bookmarkedCompanies.length) {
        items.push({
          label: 'Saved companies',
          value: this.bookmarkedCompanies.length,
          caption: 'Ready to revisit',
          icon: Star,
        })
      }

      if (!items.length) {
        items.push({
          label: 'Profile status',
          value: this.resolveResumeStatus() === 'active' ? 'Ready' : 'Building',
          caption: 'Your market view will improve as details arrive',
          icon: FileText,
        })
      }

      return items.slice(0, 4)
    },
    matchingTitle() {
      if (this.profile.role && this.jobs.length) {
        return `Roles matching ${this.roleLabel}`
      }
      if (this.jobs.length) {
        return 'Roles matching your profile'
      }
      return 'No strong job matches today'
    },
    hiringCompaniesTitle() {
      if (this.profile.state) {
        return `Employers hiring in ${this.profile.state}`
      }
      return 'Employers with active roles'
    },
    hiringCompaniesQuery() {
      return {
        active_jobs: '1',
        ...this.companyStateQuery,
      }
    },
    newJobsThisWeek() {
      return this.jobs.filter((job) => this.isWithinLastDays(job.job_date, 7))
    },
    applicationsThisWeek() {
      return this.applications.filter((application) => this.isWithinLastDays(application.application_date, 7))
    },
    hasDatedWeeklyActivity() {
      return this.newJobsThisWeek.length > 0 || this.applicationsThisWeek.length > 0
    },
    activitySectionEyebrow() {
      return this.hasDatedWeeklyActivity ? "What's new this week" : 'Active right now'
    },
    whatsNewTitle() {
      if (this.newJobsThisWeek.length) {
        return `${this.newJobsThisWeek.length} matching ${this.newJobsThisWeek.length === 1 ? 'role' : 'roles'} appeared recently`
      }
      if (this.applicationsThisWeek.length) {
        return `${this.applicationsThisWeek.length} application ${this.applicationsThisWeek.length === 1 ? 'sent' : 'sent'} this week`
      }
      if (this.hiringCompanies.length) {
        return 'Employers are hiring now'
      }
      return 'Worth watching this week'
    },
    whatsNewCopy() {
      if (this.newJobsThisWeek.length) {
        return 'These roles include recent posting dates.'
      }
      if (this.applicationsThisWeek.length) {
        return 'A quick reminder of activity you started this week.'
      }
      if (this.hiringCompanies.length) {
        return 'No dated new roles are visible, but these employers are active right now.'
      }
      return 'No dated weekly change is visible yet, so these are worth watching rather than new updates.'
    },
    whatsNewItems() {
      const items = []

      if (this.newJobsThisWeek.length) {
        const job = this.newJobsThisWeek[0]
        items.push({
          key: 'new_matching_job_this_week',
          title: job.job_title,
          copy: `${job.company_name}${job.job_location ? ` · ${job.job_location}` : ''}`,
          to: { name: 'ContractorViewJob', params: { jobId: job.job_slug || '#' } },
          icon: BriefcaseBusiness,
        })
      }

      if (this.applicationsThisWeek.length) {
        items.push({
          key: 'applications_this_week',
          title: `${this.applicationsThisWeek.length} application ${this.applicationsThisWeek.length === 1 ? 'sent' : 'sent'} this week`,
          copy: 'Keep track of roles you have already started.',
          to: '/contractor/applied-jobs',
          icon: CheckCircle2,
        })
      }

      if (this.hiringCompanies.length) {
        const company = this.hiringCompanies[0]
        items.push({
          key: 'hiring_company_active_now',
          title: `${company.name} is hiring`,
          copy: company.office_address || company.summary || 'Employer with active jobs.',
          to: { name: 'ContractorCompanyPage', params: { slug: company.slug } },
          icon: Building2,
        })
      }

      if (this.projectPreview.length) {
        const project = this.projectPreview[0]
        items.push({
          key: 'project_to_watch',
          title: project.name || 'Project to watch',
          copy: `${project.company_name}${project.location_label ? ` · ${project.location_label}` : ''}`,
          to: { name: 'ContractorCompanyPage', params: { slug: project.company_slug } },
          icon: MapPinned,
        })
      }

      if (this.topCommodities.length) {
        const commodity = this.topCommodities[0]
        items.push({
          key: 'commodity_theme_to_watch',
          title: `${commodity.name} is visible in your market`,
          copy: `Appears across ${commodity.count} surfaced ${commodity.count === 1 ? 'result' : 'results'}.`,
          to: { name: 'ContractorCompanySearch', query: { commodities: [commodity.name] } },
          icon: Gem,
        })
      }

      if (!items.length) {
        items.push({
          key: 'refine_market_details_empty_week',
          title: 'Keep your market view useful',
          copy: 'Add role, location, availability, and resume details so Screened can surface better activity.',
          to: '/contractor/profile/edit',
          icon: Pencil,
        })
      }

      return items.slice(0, 3)
    },
    heroEyebrow() {
      if (this.canSearchCompanies) {
        return this.profile.state ? `${this.industryLabel} intelligence · ${this.profile.state}` : `${this.industryLabel} intelligence`
      }
      return this.profile.role ? `For ${this.roleLabel}` : 'Candidate market view'
    },
    heroHeadline() {
      if (this.canSearchCompanies && this.profile.state) {
        return `Explore ${this.industryLabel.toLowerCase()} employers around ${this.profile.state}`
      }
      if (this.canSearchCompanies) {
        return `Explore ${this.industryLabel.toLowerCase()} employers and projects`
      }
      if (this.profile.role) {
        return `Find opportunities connected to ${this.roleLabel}`
      }
      return 'Explore employers in your market'
    },
    heroSubtext() {
      if (this.canSearchCompanies) {
        const reason = this.profile.role ? ` because you are registered as a ${this.roleLabel}` : ` based on your selected industry`
        const place = this.profile.state ? ` and your ${this.profile.state} location` : ''
        return `Start with companies, projects, commodities, and active jobs${reason}${place}.`
      }

      if (this.jobs.length) {
        return `Start with roles connected to ${this.roleLabel} and use your profile details only when they help you apply faster.`
      }

      if (this.resumeNeedsAttention) {
        return 'We are preparing the information that powers better matches. You can still browse opportunities while your resume and profile details are being readied.'
      }

      return 'Start exploring roles, employers, and next moves connected to your industry.'
    },
    relevanceSummary() {
      const parts = []
      if (this.profile.role) parts.push(this.roleLabel)
      if (this.profile.state) parts.push(this.profile.state)
      if (this.bookmarkedCompanies.length) parts.push('saved companies')
      return parts.length ? `Personalized from ${parts.join(', ')}` : 'Based on available market data'
    },
    marketSnapshot() {
      const items = []

      if (this.canSearchCompanies) {
        items.push({
          label: this.profile.state ? `Companies in ${this.profile.state}` : 'Companies surfaced',
          value: this.formatCount(this.marketTotals.companies, this.marketCompanies.length),
        })
        items.push({
          label: 'Hiring employers',
          value: this.formatCount(this.marketTotals.hiringCompanies, this.hiringCompanies.length),
        })
        items.push({
          label: 'Projects surfaced',
          value: this.formatCount(this.marketTotals.projects, this.marketProjects.length),
        })
        items.push({
          label: 'Saved companies',
          value: this.bookmarkedCompanies.length,
        })
        return items
      }

      items.push({
        label: 'Matching jobs',
        value: this.jobs.length,
      })
      items.push({
        label: 'Applications',
        value: this.applications.length,
      })
      items.push({
        label: 'Resume status',
        value: this.resolveResumeStatus() === 'active' ? 'Ready' : 'Pending',
      })
      items.push({
        label: 'Role',
        value: this.profile.role ? 'Set' : 'Open',
      })
      return items
    },
    companyStateQuery() {
      return this.homeStateFilterValue ? { states: [this.homeStateFilterValue] } : {}
    },
    projectStateQuery() {
      return this.homeStateFilterValue ? { mode: 'project', project_states: [this.homeStateFilterValue] } : { mode: 'project' }
    },
    priorityCompanies() {
      if (this.savedSimilarCompanies.length) {
        return this.savedSimilarCompanies.slice(0, 8)
      }

      const seen = new Set()
      return [...this.bookmarkedCompanies, ...this.marketCompanies].filter((company) => {
        const key = company.id || company.slug
        if (!key || seen.has(key)) return false
        seen.add(key)
        return true
      }).slice(0, 8)
    },
    companyRowEyebrow() {
      if (this.savedSimilarCompanies.length) return 'Because of companies you saved'
      if (this.profile.role) return `Because you are a ${this.roleLabel}`
      return 'Relevant companies'
    },
    companyRowTitle() {
      if (this.savedSimilarCompanies.length) return 'More companies in markets you have saved'
      if (this.profile.state) return `Employers connected to ${this.profile.state}`
      return 'Companies to start with'
    },
    companyRowCopy() {
      if (this.savedSimilarCompanies.length) {
        return 'Based on commodities and company themes from your saved employers.'
      }
      if (this.profile.state) {
        return `A first pass at companies filtered through your ${this.profile.state} market context.`
      }
      return `A first pass at companies connected to ${this.industryLabel.toLowerCase()}.`
    },
    projectPreview() {
      return this.marketProjects.slice(0, 6)
    },
    projectRowEyebrow() {
      if (this.profile.state) return `Projects around ${this.profile.state}`
      return 'Project intelligence'
    },
    projectRowTitle() {
      return this.topCommodities.length ? `${this.topCommodities[0].name} and adjacent project activity` : 'Projects to watch'
    },
    projectRowCopy() {
      if (this.profile.state) {
        return `Projects surfaced from company intelligence and filtered toward your ${this.profile.state} market where possible.`
      }
      return 'Projects surfaced from company profiles, including stage, location, company, and commodities where available.'
    },
    topCommodities() {
      const counts = {}
      const sources = [
        ...this.marketCompanies,
        ...this.hiringCompanies,
        ...this.bookmarkedCompanies,
        ...this.marketProjects,
      ]

      sources.forEach((item) => {
        const commodities = Array.isArray(item.commodities) ? item.commodities : []
        commodities.forEach((commodity) => {
          const name = String(commodity || '').trim()
          if (!name) return
          counts[name] = (counts[name] || 0) + 1
        })
      })

      return Object.entries(counts)
        .map(([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
    },
    commodityRelevanceCopy() {
      if (this.bookmarkedCompanies.length) {
        return 'Themes from your saved companies and surfaced market data.'
      }
      if (this.profile.state) {
        return `Themes appearing in the ${this.profile.state} company and project slice.`
      }
      return 'Themes appearing across surfaced companies and projects.'
    },
    hiringRowTitle() {
      if (this.profile.state && this.hiringCompanies.length) return `Employers hiring in ${this.profile.state}`
      if (this.hiringCompanies.length) return 'Employers with active roles'
      return `Roles matching ${this.roleLabel}`
    },
    hiringRowCopy() {
      if (this.hiringCompanies.length) {
        return 'Company intelligence with active jobs, so research and applications stay connected.'
      }
      return `Roles currently matched from your selected industry and role.`
    },
    jobRowTitle() {
      return this.profile.role ? `Roles for ${this.roleLabel}` : 'Matching roles'
    },
    jobRowCopy() {
      return this.profile.industry ? `Matched from your ${this.industryLabel.toLowerCase()} profile.` : 'Matched from your profile details.'
    },
    matchingOpportunityCopy() {
      if (!this.jobPreview.length) {
        return 'This page will update as new roles are added in your field.'
      }

      if (this.jobPreview.some((job) => job.__screenedRoleScore >= 40)) {
        return 'Roles closest to your field of experience.'
      }

      return `${this.industryLabel} roles worth reviewing while more specific matches are added.`
    },
    recentApplications() {
      return this.applications.slice(0, 3)
    },
    scoredMatchingJobs() {
      const industryJobs = this.companyDirectoryIndustryFilter
        ? this.jobs.filter((job) => this.normalizeJobIndustry(job) === this.companyDirectoryIndustryFilter)
        : [...this.jobs]

      return industryJobs
        .map((job) => ({
          ...job,
          __screenedRoleScore: this.scoreJobForCandidate(job),
        }))
        .sort((a, b) => {
          if (b.__screenedRoleScore !== a.__screenedRoleScore) {
            return b.__screenedRoleScore - a.__screenedRoleScore
          }

          return this.getJobTimestamp(b) - this.getJobTimestamp(a)
        })
    },
    jobPreview() {
      return this.scoredMatchingJobs
        .slice(0, 6)
    },
    hiringCompanyPreview() {
      return this.hiringCompanies
        .map((company) => {
          const companyKey = this.normalizeCompanyMatchKey(company.name)
          const matchingJobs = companyKey
            ? this.scoredMatchingJobs.filter((job) => this.companyNameKeysMatch(companyKey, this.normalizeCompanyMatchKey(job.company_name)))
            : []
          const bestJobScore = matchingJobs.reduce((score, job) => Math.max(score, Number(job.__screenedRoleScore || 0)), 0)

          return {
            ...company,
            __screenedCompanyScore: this.scoreCompanyForCandidate(company, bestJobScore, matchingJobs.length),
          }
        })
        .sort((a, b) => {
          if (b.__screenedCompanyScore !== a.__screenedCompanyScore) {
            return b.__screenedCompanyScore - a.__screenedCompanyScore
          }

          return Number(b.active_jobs || 0) - Number(a.active_jobs || 0)
        })
        .slice(0, 3)
    },
    profilePrompts() {
      const prompts = []

      if (this.uploads.failed || (!this.hasActiveResume && !this.uploads.pending)) {
        prompts.push({
          key: 'upload_resume_prompt',
          title: 'Upload a current resume',
          copy: 'A current resume helps Screened prepare better matches and makes applications faster.',
          label: 'Update profile',
          to: '/contractor/profile/edit',
          icon: UploadCloud,
        })
      } else if (this.uploads.pending) {
        prompts.push({
          key: 'resume_processing_prompt',
          title: 'Resume processing is underway',
          copy: 'Your market view will improve once resume parsing has finished.',
          label: 'View profile details',
          to: '/contractor/profile/edit',
          icon: FileText,
        })
      }

      if (!this.profile.availability) {
        prompts.push({
          key: 'availability_prompt',
          title: 'Add availability',
          copy: 'Availability helps roles and applications reflect when you can actually start.',
          label: 'Add availability',
          to: '/contractor/profile/edit',
          icon: CheckCircle2,
        })
      }

      if (!this.profile.ai_description) {
        prompts.push({
          key: 'ai_summary_prompt',
          title: 'Add a short summary',
          copy: 'A concise summary can make applications easier once you choose a role.',
          label: 'Generate summary',
          action: 'generate_ai',
          icon: Sparkles,
        })
      }

      if (!this.profile.rate && !this.profile.salary) {
        prompts.push({
          key: 'rate_prompt',
          title: 'Add rate or salary guidance',
          copy: 'Compensation details help you compare roles and apply with fewer steps.',
          label: 'Add details',
          to: '/contractor/profile/edit',
          icon: BadgeDollarSign,
        })
      }

      return prompts.slice(0, 3)
    },
    primaryCta() {
      if (this.canSearchCompanies) {
        return {
          key: 'open_market_intelligence_primary',
          label: 'Open company intelligence',
          to: { name: 'ContractorCompanySearch', query: this.companyStateQuery },
          icon: Building2,
        }
      }

      if (this.jobs.length) {
        return {
          key: 'view_jobs_primary',
          label: 'View matching jobs',
          to: '/contractor/jobs',
          icon: BriefcaseBusiness,
        }
      }

      return {
        key: 'refine_profile_primary',
        label: 'Refine your market details',
        to: '/contractor/profile/edit',
        icon: Search,
      }
    },
    secondaryCta() {
      if (this.canSearchCompanies && this.projectPreview.length) {
        return {
          key: 'view_projects_secondary',
          label: 'View projects',
          to: { name: 'ContractorCompanySearch', query: this.projectStateQuery },
          icon: MapPinned,
        }
      }

      if (this.jobs.length) {
        return {
          key: 'view_jobs_secondary',
          label: 'View matching jobs',
          to: '/contractor/jobs',
          icon: BriefcaseBusiness,
        }
      }

      return {
        key: 'edit_profile_secondary',
        label: 'Refine profile details',
        to: '/contractor/profile/edit',
        icon: Pencil,
      }
    },
  },
  methods: {
    async loadPage() {
      this.toggleLoading(true)
      try {
        await this.getContractorProfile()
        await Promise.allSettled([
          this.getContractorResponses(),
          this.getUploads(),
          this.getJobs(),
          this.getApplications(),
          this.getCompanyMonthlyActivity(),
        ])
        await this.getCompanyIntelligence()
        this.trackEvent('candidate_home_viewed', {
          industry_id: this.profile.industryId || null,
          role_id: this.profile.roleId || null,
          state: this.profile.state || null,
          resume_status: this.resolveResumeStatus(),
          has_ai_description: !!this.profile.ai_description && this.profile.ai_description !== 'DECLINED',
          company_search_allowed: this.canSearchCompanies,
          companies_visible: this.priorityCompanies.length,
          projects_visible: this.projectPreview.length,
          hiring_companies_visible: this.hiringCompanies.length,
          jobs_visible: this.jobs.length,
          applications_visible: this.applications.length,
          bookmarked_companies_visible: this.bookmarkedCompanies.length,
        })
      } finally {
        this.loading = false
        this.toggleLoading(false)
      }
    },
    async getContractorProfile() {
      const response = await fetch(apiEndpoint + '/marketplace.php?getContractorAccount', {
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('contractorToken'),
        },
      })

      if (!response.ok) {
        this.$router.push('/contractor/logout')
        return
      }

      this.profile = await response.json()

      if (this.profile.photo_hash || this.profile.profile_picture_s3_key) {
        this.getImage(this.profile.photo_hash, this.profile.profile_picture_s3_key)
      }
    },
    async getContractorResponses() {
      try {
        const response = await fetch(apiEndpoint + '/marketplace.php?getContractorResponses', {
          headers: {
            Authorization: 'Bearer ' + localStorage.getItem('contractorToken'),
          },
        })
        this.responses = await response.json()
      } catch (error) {
        this.responses = []
      }
    },
    async getUploads() {
      try {
        const response = await contractorService.getUploads()
        this.uploads = {
          active: null,
          replacement: null,
          pending: null,
          failed: null,
          ...(response.data || {}),
        }
      } catch (error) {
        this.uploads = {
          active: null,
          replacement: null,
          pending: null,
          failed: null,
        }
      }
    },
    async getJobs() {
      try {
        const response = await contractorService.getJobs()
        this.jobs = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.jobs = []
      }
    },
    async getApplications() {
      try {
        const response = await contractorService.getApplications()
        this.applications = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        this.applications = []
      }
    },
    async getCompanyIntelligence() {
      try {
        const access = await getContractorMiningCompanySearchAccess()
        this.canSearchCompanies = !!access.data?.allowed
        this.companyAccessLoaded = true
      } catch (error) {
        this.canSearchCompanies = false
        this.companyAccessLoaded = true
      }

      if (!this.canSearchCompanies) {
        return
      }

      await Promise.allSettled([
        this.getBookmarkedCompanies(),
        this.getMarketCompanies(),
        this.getHiringCompanies(),
        this.getMarketProjects(),
      ])

      await this.getSavedSimilarCompanies()
    },
    async getBookmarkedCompanies() {
      try {
        const response = await getContractorMiningCompanies({
          mode: 'company',
          bookmarked_only: 1,
          per_page: 4,
          page: 1,
        })
        this.bookmarkedCompanies = Array.isArray(response.data?.items) ? response.data.items : []
      } catch (error) {
        this.bookmarkedCompanies = []
      }
    },
    async getMarketCompanies() {
      try {
        const response = await getContractorMiningCompanies({
          mode: 'company',
          ...this.buildCompanyIndustryParams(),
          ...this.buildStateParams(),
          per_page: 8,
          page: 1,
        })
        this.marketCompanies = Array.isArray(response.data?.items) ? response.data.items : []
        this.marketTotals.companies = response.data?.pagination?.total ?? response.data?.total ?? null
      } catch (error) {
        this.marketCompanies = []
      }
    },
    async getHiringCompanies() {
      try {
        const params = {
          mode: 'company',
          ...this.buildCompanyIndustryParams(),
          has_active_jobs: true,
          sort: 'name_asc',
          per_page: 50,
          page: 1,
        }
        const response = await getContractorMiningCompanies(params)
        const firstPageCompanies = Array.isArray(response.data?.items) ? response.data.items : []
        const total = response.data?.pagination?.total ?? response.data?.total ?? firstPageCompanies.length
        const perPage = response.data?.pagination?.per_page ?? params.per_page
        const totalPages = Math.ceil(total / perPage)
        const extraPageCount = Math.max(0, Math.min(totalPages - 1, 4))

        if (extraPageCount > 0) {
          const extraResponses = await Promise.all(
            Array.from({ length: extraPageCount }, (_, index) => getContractorMiningCompanies({
              ...params,
              page: index + 2,
            }))
          )
          this.hiringCompanies = [
            ...firstPageCompanies,
            ...extraResponses.flatMap((extraResponse) => (
              Array.isArray(extraResponse.data?.items) ? extraResponse.data.items : []
            )),
          ]
        } else {
          this.hiringCompanies = firstPageCompanies
        }

        this.marketTotals.hiringCompanies = total
      } catch (error) {
        this.hiringCompanies = []
      }
    },
    async getMarketProjects() {
      try {
        const response = await getContractorMiningCompanies({
          mode: 'project',
          ...this.buildProjectStateParams(),
          per_page: 6,
          page: 1,
        })
        this.marketProjects = Array.isArray(response.data?.items) ? response.data.items : []
        this.marketTotals.projects = response.data?.pagination?.total ?? response.data?.total ?? null
      } catch (error) {
        this.marketProjects = []
      }
    },
    async getCompanyMonthlyActivity() {
      if (!this.profile.industry && !this.profile.industryId) {
        this.resetCompanyMonthlyActivity()
        return
      }

      const params = {
        ...this.buildCompanyActivityIndustryParams(),
        industry_id: this.profile.industryId || null,
        days: 30,
      }
      this.companyMonthlyActivityRequest = params
      this.companyMonthlyActivityError = null

      try {
        const response = await getContractorCompanyRecentActivity(params)
        this.companyMonthlyActivity = {
          recent_key_hires: Number(response.data?.recent_key_hires || 0),
          capital_raisings: Number(response.data?.capital_raisings || 0),
          matching_companies: Number(response.data?.matching_companies || 0),
          industries: Array.isArray(response.data?.industries) ? response.data.industries : [],
          days: Number(response.data?.days || 30),
          start_date: response.data?.start_date || null,
          end_date: response.data?.end_date || null,
        }
      } catch (error) {
        this.companyMonthlyActivityError = {
          message: error?.message || 'Failed to fetch monthly company activity',
          status: error?.response?.status || null,
          data: error?.response?.data || null,
        }
        console.error('Failed to fetch monthly company activity', this.companyMonthlyActivityError)
        this.resetCompanyMonthlyActivity()
      }
    },
    resetCompanyMonthlyActivity() {
      this.companyMonthlyActivity = {
        recent_key_hires: 0,
        capital_raisings: 0,
        matching_companies: 0,
        industries: [],
        days: 30,
        start_date: null,
        end_date: null,
      }
    },
    async getSavedSimilarCompanies() {
      const savedCommodities = this.collectCommodities(this.bookmarkedCompanies).slice(0, 3)
      if (!savedCommodities.length) {
        this.savedSimilarCompanies = []
        return
      }

      try {
        const response = await getContractorMiningCompanies({
          mode: 'company',
          commodities: savedCommodities,
          per_page: 8,
          page: 1,
        })
        const savedIds = new Set(this.bookmarkedCompanies.map((company) => company.id || company.slug))
        this.savedSimilarCompanies = (Array.isArray(response.data?.items) ? response.data.items : [])
          .filter((company) => !savedIds.has(company.id || company.slug))
      } catch (error) {
        this.savedSimilarCompanies = []
      }
    },
    buildStateParams() {
      return this.homeStateFilterValue ? { states: [this.homeStateFilterValue] } : {}
    },
    buildCompanyIndustryParams() {
      return this.companyDirectoryIndustryFilter ? { industries: [this.companyDirectoryIndustryFilter] } : {}
    },
    buildCompanyActivityIndustryParams() {
      const industry = String(this.profile.industry || '').trim()
      return industry ? { industries: [industry] } : {}
    },
    buildProjectStateParams() {
      return this.homeStateFilterValue ? { project_states: [this.homeStateFilterValue] } : {}
    },
    normalizeCompanyDirectoryIndustry(value) {
      const normalized = String(value || '').trim().toLowerCase()
      if (!normalized) {
        return ''
      }

      if (normalized.includes('energy')) {
        return 'energy'
      }

      if (
        normalized.includes('mining') ||
        normalized.includes('geoscience') ||
        normalized.includes('geology') ||
        normalized.includes('exploration')
      ) {
        return 'mining'
      }

      return ''
    },
    normalizeJobIndustry(job) {
      const value = String(job?.job_industry || '').toLowerCase()

      if (value.includes('energy')) {
        return 'energy'
      }

      if (value.includes('mining')) {
        return 'mining'
      }

      return ''
    },
    normalizeAustralianState(value) {
      const normalized = String(value || '').trim().toLowerCase()
      if (!normalized) {
        return ''
      }

      const states = {
        act: 'ACT',
        'australian capital territory': 'ACT',
        nsw: 'NSW',
        'new south wales': 'NSW',
        qld: 'QLD',
        queensland: 'QLD',
        wa: 'WA',
        'western australia': 'WA',
        sa: 'SA',
        'south australia': 'SA',
        vic: 'VIC',
        victoria: 'VIC',
        tas: 'TAS',
        tasmania: 'TAS',
        nt: 'NT',
        'northern territory': 'NT',
        overseas: 'Overseas',
      }

      return states[normalized] || String(value || '').trim()
    },
    collectCommodities(items) {
      const seen = new Set()
      const commodities = []

      items.forEach((item) => {
        const values = Array.isArray(item.commodities) ? item.commodities : []
        values.forEach((value) => {
          const commodity = String(value || '').trim()
          if (!commodity || seen.has(commodity)) return
          seen.add(commodity)
          commodities.push(commodity)
        })
      })

      return commodities
    },
    async copyProfileLinkToClipboard() {
      if (!this.profile.uuid) {
        return
      }

      try {
        await navigator.clipboard.writeText(process.env.VUE_APP_WEBSITE_URL + '/contractors/' + this.profile.uuid)
        this.trackEvent('candidate_home_cta_clicked', {
          cta: 'copy_public_profile_link',
          source_section: 'profile_tools',
        })
        this.$root.notify('Your public profile URL has been copied to clipboard.', 'success')
      } catch (err) {
        this.$root.notify('Profile link could not be copied right now.', 'error')
      }
    },
    async generateAIDescription(sourceSection) {
      if (this.loadingAIDescription) {
        return
      }

      this.loadingAIDescription = true
      this.trackEvent('candidate_ai_description_generated_started', {
        source_section: sourceSection,
      })

      try {
        const response = await contractorService.getDescription()
        if (response.data?.description) {
          this.profile.ai_description = response.data.description
          this.trackEvent('candidate_ai_description_generated', {
            source_section: sourceSection,
          })
          this.$root.notify('Your profile summary is ready.', 'success')
        }
      } catch (error) {
        this.$root.notify('We ran into an issue while generating your profile summary. Please try again later.', 'error')
      } finally {
        this.loadingAIDescription = false
      }
    },
    downloadResume() {
      this.toggleLoading(true)
      this.trackEvent('candidate_resume_action_clicked', {
        action: 'download_resume',
        resume_status: this.resolveResumeStatus(),
      })

      contractorService.getResume()
        .then(response => {
          const contentType = response.headers?.['content-type'] || response.headers?.get?.('content-type')

          if (response.status === 200 && (
            contentType === 'application/pdf' ||
            contentType === 'application/msword' ||
            contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
          )) {
            const outputFileName = `${this.profile.profile_hash}.${this.profile.file_extension || 'pdf'}`
            const myBlob = response.data
            const url = window.URL.createObjectURL(myBlob)
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', outputFileName)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          } else {
            this.$root.notify('Your resume could not be retrieved at this time. Please try again later.', 'error')
          }
        })
        .catch(() => {
          this.$root.notify('Something went wrong while fetching your resume. Please try again.', 'error')
        })
        .finally(() => {
          this.toggleLoading(false)
        })
    },
    getImage(hash, s3Key) {
      this.profilePicture = genericService.getProfilePictureLink(hash, s3Key, true)
    },
    getCompanyResearchForJob(job) {
      if (job.company_summary) {
        return job.company_summary
      }

      const company = this.findCompanyForJob(job)
      if (!company) {
        return ''
      }

      if (company.summary) {
        return company.summary
      }

      const parts = []
      if (company.commodities && company.commodities.length) {
        parts.push(`Commodities: ${company.commodities.slice(0, 3).join(', ')}`)
      }
      if (company.office_address) {
        parts.push(company.office_address)
      }

      return parts.join(' · ')
    },
    scoreJobForCandidate(job) {
      let score = 0

      if (!this.companyDirectoryIndustryFilter || this.normalizeJobIndustry(job) === this.companyDirectoryIndustryFilter) {
        score += 20
      }

      const haystack = [
        job.job_title,
        job.job_role,
        job.job_category,
        job.category,
        job.job_subcategory,
        job.subcategory,
      ].map((value) => String(value || '').toLowerCase()).join(' ')

      const roleKeywords = this.getRoleKeywords(this.roleLabel)
      roleKeywords.forEach((keyword) => {
        if (!keyword) return
        if (haystack.includes(keyword)) {
          score += 40
        }
      })

      const title = String(job.job_title || '').toLowerCase()
      if (roleKeywords.some((keyword) => keyword && title.includes(keyword))) {
        score += 30
      }

      if (this.profile.state && this.normalizeJobState(job) === this.homeStateFilterValue) {
        score += 8
      }

      if (this.isWithinLastDays(job.job_date, 14)) {
        score += 5
      }

      return score
    },
    scoreCompanyForCandidate(company, bestJobScore = 0, matchingJobCount = 0) {
      let score = 0

      if (bestJobScore > 0) {
        score += bestJobScore
      }

      if (matchingJobCount > 0) {
        score += Math.min(20, matchingJobCount * 5)
      }

      if (!this.companyDirectoryIndustryFilter || this.normalizeCompanyIndustry(company) === this.companyDirectoryIndustryFilter) {
        score += 15
      }

      if (this.profile.state && this.companyMatchesHomeState(company)) {
        score += 8
      }

      const activeJobs = Number(company.active_jobs || 0)
      if (activeJobs > 0) {
        score += Math.min(10, activeJobs)
      }

      const roleKeywords = this.getRoleKeywords(this.roleLabel)
      const haystack = [
        company.name,
        company.summary,
        company.industry,
        company.industry_subcategory,
        Array.isArray(company.commodities) ? company.commodities.join(' ') : '',
      ].map((value) => String(value || '').toLowerCase()).join(' ')

      roleKeywords.forEach((keyword) => {
        if (!keyword) return
        if (haystack.includes(keyword)) {
          score += 10
        }
      })

      return score
    },
    normalizeCompanyIndustry(company) {
      return this.normalizeCompanyDirectoryIndustry(company?.industry || company?.industry_slug || '')
    },
    normalizeCompanyMatchKey(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/&/g, 'and')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\b(ltd|limited|pty|inc|corp|corporation|company|co)\b/g, '')
        .replace(/\s+/g, ' ')
        .trim()
    },
    companyNameKeysMatch(companyKey, jobCompanyKey) {
      if (!companyKey || !jobCompanyKey) {
        return false
      }

      return companyKey === jobCompanyKey
        || (companyKey.length > 4 && jobCompanyKey.includes(companyKey))
        || (jobCompanyKey.length > 4 && companyKey.includes(jobCompanyKey))
    },
    companyMatchesHomeState(company) {
      if (!this.homeStateFilterValue) {
        return false
      }

      return this.normalizeAustralianState(company?.office_address) === this.homeStateFilterValue
        || String(company?.office_address || '').toLowerCase().includes(this.homeStateFilterValue.toLowerCase())
    },
    getRoleKeywords(role) {
      const normalized = String(role || '').toLowerCase()
      const keywords = new Set()
      const stopWords = new Set(['and', 'the', 'for', 'with', 'assistant', 'officer', 'worker', 'specialist'])

      if (normalized.length > 2) {
        keywords.add(normalized)
      }

      normalized
        .split(/[^a-z0-9]+/)
        .filter((part) => part.length > 3 && !stopWords.has(part))
        .forEach((part) => keywords.add(part))

      const mappings = [
        {
          match: ['field assistant', 'field technician', 'field hand'],
          keywords: ['field assistant', 'field technician', 'exploration assistant', 'geological assistant', 'geology technician', 'sample technician', 'core yard', 'drill offsider'],
        },
        {
          match: ['geologist', 'geoscientist', 'geoscience', 'development geologist'],
          keywords: ['geologist', 'geoscientist', 'geology', 'exploration geologist', 'mine geologist', 'project geologist', 'development geologist'],
        },
        {
          match: ['mining engineer', 'mine engineer'],
          keywords: ['mining engineer', 'mine engineer', 'planning engineer', 'production engineer', 'drill and blast'],
        },
        {
          match: ['metallurgist', 'process engineer', 'processing'],
          keywords: ['metallurgist', 'metallurgy', 'process engineer', 'processing', 'plant metallurgist'],
        },
        {
          match: ['surveyor', 'survey'],
          keywords: ['surveyor', 'mine surveyor', 'survey technician', 'survey assistant'],
        },
        {
          match: ['operator', 'truck driver', 'machine operator'],
          keywords: ['operator', 'truck operator', 'machine operator', 'loader operator', 'excavator operator', 'haul truck'],
        },
        {
          match: ['electrician', 'electrical'],
          keywords: ['electrician', 'electrical', 'auto electrician', 'maintenance electrician'],
        },
        {
          match: ['mechanic', 'fitter', 'maintenance'],
          keywords: ['mechanic', 'fitter', 'maintenance fitter', 'diesel fitter', 'hd fitter', 'mechanical fitter'],
        },
        {
          match: ['environment', 'environmental'],
          keywords: ['environmental', 'environment advisor', 'environment officer', 'rehabilitation'],
        },
        {
          match: ['health safety', 'hse', 'safety'],
          keywords: ['hse', 'safety', 'health and safety', 'whs', 'ohs'],
        },
      ]

      mappings.forEach((group) => {
        if (group.match.some((term) => normalized.includes(term))) {
          group.keywords.forEach((keyword) => keywords.add(keyword))
        }
      })

      return [...keywords]
    },
    normalizeJobState(job) {
      const location = String(job?.job_location || '').toLowerCase()
      if (!location) {
        return ''
      }

      const stateMatchers = [
        { value: 'ACT', full: 'australian capital territory', abbr: /(^|[\s,()&/-])act(?=$|[\s,()&/-])/i },
        { value: 'NSW', full: 'new south wales', abbr: /(^|[\s,()&/-])nsw(?=$|[\s,()&/-])/i },
        { value: 'QLD', full: 'queensland', abbr: /(^|[\s,()&/-])qld(?=$|[\s,()&/-])/i },
        { value: 'WA', full: 'western australia', abbr: /(^|[\s,()&/-])wa(?=$|[\s,()&/-])/i },
        { value: 'SA', full: 'south australia', abbr: /(^|[\s,()&/-])sa(?=$|[\s,()&/-])/i },
        { value: 'VIC', full: 'victoria', abbr: /(^|[\s,()&/-])vic(?=$|[\s,()&/-])/i },
        { value: 'TAS', full: 'tasmania', abbr: /(^|[\s,()&/-])tas(?=$|[\s,()&/-])/i },
        { value: 'NT', full: 'northern territory', abbr: /(^|[\s,()&/-])nt(?=$|[\s,()&/-])/i },
      ]

      const originalLocation = String(job?.job_location || '')
      const matched = stateMatchers.find((matcher) => location.includes(matcher.full) || matcher.abbr.test(originalLocation))
      return matched?.value || ''
    },
    getJobTimestamp(job) {
      const value = job?.job_date
      if (!value) {
        return 0
      }

      const date = new Date(`${value}Z`)
      return Number.isNaN(date.getTime()) ? 0 : date.getTime()
    },
    findCompanyForJob(job) {
      const companyId = String(job.company_id || '')
      const companySlug = String(job.company_slug || '').toLowerCase()
      const companyName = String(job.company_name || '').toLowerCase()
      if (!companyId && !companySlug && !companyName) {
        return null
      }

      return [
        ...this.hiringCompanies,
        ...this.marketCompanies,
        ...this.bookmarkedCompanies,
        ...this.savedSimilarCompanies,
      ].find((company) => {
        const id = String(company.id || '')
        const slug = String(company.slug || '').toLowerCase()
        const name = String(company.name || '').toLowerCase()
        return (companyId && id === companyId)
          || (companySlug && slug === companySlug)
          || (companyName && name === companyName)
      }) || null
    },
    companyInitial(name) {
      return String(name || 'C').trim().charAt(0).toUpperCase()
    },
    getCompanyJobsCount(company) {
      const directCountFields = [
        company?.active_jobs,
        company?.active_jobs_count,
        company?.live_jobs,
        company?.live_jobs_count,
        company?.jobs_count,
        company?.job_count,
      ]

      for (const value of directCountFields) {
        const count = Number(value)
        if (Number.isFinite(count) && count >= 0) {
          return count
        }
      }

      const companyKeys = new Set([
        String(company?.slug || '').toLowerCase(),
        String(company?.name || '').toLowerCase(),
      ].filter(Boolean))

      return this.jobs.filter((job) => {
        const jobKeys = [
          job.company_slug,
          job.company_name,
        ].map((value) => String(value || '').toLowerCase())

        return jobKeys.some((key) => companyKeys.has(key))
      }).length
    },
    formatCompanyJobsCount(company) {
      const count = this.getCompanyJobsCount(company)
      return `${count.toLocaleString()} ${count === 1 ? 'job' : 'jobs'}`
    },
    getCompanyProjectCount(company) {
      if (Array.isArray(company?.key_projects)) {
        return company.key_projects.length
      }

      const directCountFields = [
        company?.projects_count,
        company?.project_count,
        company?.active_projects,
        company?.active_projects_count,
      ]

      for (const value of directCountFields) {
        const count = Number(value)
        if (Number.isFinite(count) && count >= 0) {
          return count
        }
      }

      return this.marketProjects.filter((project) => {
        const companyId = String(company?.id || '')
        const projectCompanyId = String(project?.company_id || '')
        const companySlug = String(company?.slug || '').toLowerCase()
        const projectCompanySlug = String(project?.company_slug || '').toLowerCase()
        const companyName = String(company?.name || '').toLowerCase()
        const projectCompanyName = String(project?.company_name || '').toLowerCase()

        return (companyId && projectCompanyId && companyId === projectCompanyId)
          || (companySlug && projectCompanySlug && companySlug === projectCompanySlug)
          || (companyName && projectCompanyName && companyName === projectCompanyName)
      }).length
    },
    formatCompanyProjectsCount(company) {
      const count = this.getCompanyProjectCount(company)
      return `${count.toLocaleString()} ${count === 1 ? 'project' : 'projects'}`
    },
    formatMoney(value) {
      const amount = this.parseMoneyValue(value)
      if (amount === null) {
        return 'Not listed'
      }

      if (amount >= 1000000000) {
        return `$${(amount / 1000000000).toFixed(amount >= 10000000000 ? 0 : 1)}b`
      }

      if (amount >= 1000000) {
        return `$${(amount / 1000000).toFixed(amount >= 10000000 ? 0 : 1)}m`
      }

      if (amount >= 1000) {
        return `$${Math.round(amount / 1000)}k`
      }

      return `$${Math.round(amount).toLocaleString()}`
    },
    parseMoneyValue(value) {
      if (value === null || value === undefined || value === '') {
        return null
      }

      if (typeof value === 'number') {
        return Number.isFinite(value) ? value : null
      }

      const normalized = String(value)
        .toLowerCase()
        .replace(/aud|a\$|\$|,/g, '')
        .trim()
      const match = normalized.match(/^(-?\d+(?:\.\d+)?)(\s*[mbk])?/)
      if (!match) {
        return null
      }

      const amount = Number(match[1])
      if (!Number.isFinite(amount)) {
        return null
      }

      const suffix = (match[2] || '').trim()
      if (suffix === 'b') return amount * 1000000000
      if (suffix === 'm') return amount * 1000000
      if (suffix === 'k') return amount * 1000
      return amount
    },
    formatCount(total, fallback) {
      if (typeof total === 'number' && total > 0) {
        return total.toLocaleString()
      }
      return fallback || 0
    },
    isWithinLastDays(value, days) {
      if (!value) {
        return false
      }

      const date = new Date(`${value}Z`)
      if (Number.isNaN(date.getTime())) {
        return false
      }

      const cutoff = new Date()
      cutoff.setDate(cutoff.getDate() - days)
      return date >= cutoff
    },
    formatDate(value) {
      if (!value) {
        return 'recently'
      }

      const date = new Date(`${value}Z`)
      if (Number.isNaN(date.getTime())) {
        return 'recently'
      }

      return date.toLocaleDateString('en-AU', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      })
    },
    formatJobRate(job) {
      if (job.rate_category && job.rate_category !== 'custom_rate') {
        return `${String(job.rate_category).replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()).replace(/\s*Rate\s*/gi, '').trim()} rate`
      }

      if (job.job_rate && job.job_rate_type) {
        return `$${job.job_rate} / ${job.job_rate_type}`
      }

      return ''
    },
    resolveResumeStatus() {
      if (this.uploads.pending) {
        return 'pending'
      }
      if (this.uploads.failed) {
        return 'failed'
      }
      if (this.uploads.replacement) {
        return 'replacement_processing'
      }
      if (this.uploads.active || this.profile.has_resume) {
        return 'active'
      }
      return 'missing'
    },
    trackCta(cta, destination) {
      this.trackEvent('candidate_home_cta_clicked', {
        cta,
        destination,
      })
    },
    trackCompanyClick(company, sourceSection) {
      this.trackEvent('candidate_company_card_clicked', {
        company_id: company.id || null,
        company_slug: company.slug || null,
        source_section: sourceSection,
        is_bookmarked: !!company.is_bookmarked,
      })
    },
    trackProjectClick(project, sourceSection) {
      this.trackEvent('candidate_project_card_clicked', {
        project_id: project.id || null,
        company_slug: project.company_slug || null,
        source_section: sourceSection,
      })
    },
    trackJobClick(job, sourceSection) {
      this.trackEvent('candidate_job_card_clicked', {
        job_id: job.job_id || job.application_id || null,
        job_slug: job.job_slug || null,
        source_section: sourceSection,
        has_applied: !!job.application_date,
      })
    },
    trackEvent(name, payload = {}) {
      const detail = {
        name,
        payload: {
          ...payload,
          route: this.$route.fullPath,
        },
      }

      if (window.dataLayer && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({
          event: name,
          ...detail.payload,
        })
      }

      window.dispatchEvent(new CustomEvent('screened:analytics', { detail }))
    },
  },
  created() {
    this.loadPage()
  },
}
</script>
